import { WebAppAppointmentCreationRequest } from "@physiofit-digital/physiofit_types";
import { httpsCallable } from "firebase/functions";
import {
  getAppointmentCustomPreferenceIds,
  getAppointmentData,
  getAppointmentPatientIsSelfPayer,
  getAppointmentRecipeAssets,
  getAppointmentSelfPayerAdditionalTreatments,
  getAppointmentSelfPayerDurationMinutes,
  getAppointmentTherapyDepartment,
  getRebookingWithoutRecipe,
} from "../../../core/store/appointmentSlice";
import {
  getConversionTrackingConfig,
  getCurrentPracticeCustomAppointmentOptions,
} from "../../../core/store/chainSlice";
import { useMainStore } from "../../../core/store/mainStore";
import { addValueIfExists } from "../../../core/utils/addValueIfExists";
import { getUuid } from "../../../core/utils/helpers";
import {
  BOOKING_COMPLETED_EVENT,
  pushBookingEvent,
} from "../../../core/utils/tracking";
import { functions } from "../../../firebaseConfig";
import { customPreferencesToSend } from "./detailOptions";
import { buildSelfPayerInfoNote } from "./selfPayerTreatmentOptions";
import { useAppointmentContactRequirements } from "./useAppointmentContactRequirements";

export const useCompleteAppointment = () => {
  const appointmentData = useMainStore(getAppointmentData);
  const resetValues = useMainStore((state) => state.resetAppointmentState);
  const trackingConfig = useMainStore(getConversionTrackingConfig);
  const recipeAssets = useMainStore(getAppointmentRecipeAssets);
  const appointmentPatientIsSelfPayer = useMainStore(
    getAppointmentPatientIsSelfPayer,
  );
  const isRebookingWithoutRecipe = useMainStore(getRebookingWithoutRecipe);
  const therapyDepartment = useMainStore(getAppointmentTherapyDepartment);
  const selfPayerDurationMinutes = useMainStore(
    getAppointmentSelfPayerDurationMinutes,
  );
  const selfPayerAdditionalTreatments = useMainStore(
    getAppointmentSelfPayerAdditionalTreatments,
  );
  const customOptions = useMainStore(
    getCurrentPracticeCustomAppointmentOptions,
  );
  const customPreferenceIds = useMainStore(getAppointmentCustomPreferenceIds);
  const { showEmail, birthdayRequired } = useAppointmentContactRequirements();
  const handleComplete = async () => {
    const call = httpsCallable(functions, "patientWebAppAppointment");
    if (
      !appointmentData.practiceId ||
      !appointmentData.chainId ||
      !appointmentData.requestType ||
      !appointmentData.countryCode ||
      !appointmentData.nationalPhone ||
      !appointmentData.firstName ||
      !appointmentData.lastName ||
      (showEmail && !appointmentData.email) ||
      (birthdayRequired && !appointmentData.birthday)
    ) {
      console.error("Incomplete appointment data:", appointmentData);
      return;
    }
    const customPreferences = customPreferencesToSend(
      customPreferenceIds,
      customOptions,
    );
    const callData: WebAppAppointmentCreationRequest = {
      practiceId: appointmentData.practiceId as string,
      chainId: appointmentData.chainId as string,
      phone: {
        countryCode: appointmentData.countryCode as string,
        nationalNumber: appointmentData.nationalPhone as string,
        phoneNumber:
          appointmentData.countryCode + "" + appointmentData.nationalPhone,
      },
      personalInfo: {
        firstName: appointmentData.firstName as string,
        lastName: appointmentData.lastName as string,
        ...addValueIfExists("email", appointmentData.email),
        ...addValueIfExists("birthday", appointmentData.birthday),
      },
      appointmentToChangeDate: appointmentData.appointmentToModifyDateTime,
      requestType: appointmentData.requestType,
      appointmentPatientIsSelfPayer: appointmentPatientIsSelfPayer,
      ...(appointmentData.timeSlots
        ? {
            timeSlotInfo: {
              selectedTimeSlots: appointmentData.timeSlots as string[],
            },
          }
        : {}),
      ...(recipeAssets
        ? {
            recipe: {
              recipeId: getUuid(),
              assets: recipeAssets,
              uploadedAt: new Date().toISOString(),
            },
          }
        : {}),
      ...addValueIfExists("rebookingWithoutRecipe", isRebookingWithoutRecipe),
      ...addValueIfExists("therapyDepartment", therapyDepartment),
      ...addValueIfExists(
        "appointmentPreferences",
        appointmentData.appointmentPreferences,
      ),
      ...(customPreferences.length > 0
        ? { appointmentCustomPreferences: customPreferences }
        : {}),
      ...addValueIfExists(
        "patientNote",
        appointmentData.appointmentPatientNote,
      ),
      ...addValueIfExists(
        "infoNote",
        buildSelfPayerInfoNote(
          selfPayerDurationMinutes,
          selfPayerAdditionalTreatments,
        ),
      ),
    };
    await call(callData);

    if (trackingConfig?.enabled && callData.requestType === "booking") {
      pushBookingEvent(BOOKING_COMPLETED_EVENT, {
        chainId: callData.chainId,
        practiceId: callData.practiceId,
        requestType: callData.requestType,
      });
    }

    resetValues();
  };

  return handleComplete;
};
